'use client';

import { ApolloNextAppProvider } from '@apollo/experimental-nextjs-app-support';
import { useSession } from '@ory/elements-react/client';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { useAuth } from '@/hooks';
import { getClient, logger, setToken } from '@/utils';

const NEXT_PUBLIC_JWT_REFRESH_TIME =
  Number(process.env.NEXT_PUBLIC_JWT_REFRESH_TIME) || 7200000; // defaults to 2 hours

const ApolloClientProvider = ({ children }: React.PropsWithChildren) => {
  const { session, refetch, isLoading: sessionIsLoading } = useSession();
  const { resetState } = useAuth();
  const router = useRouter();

  if (!NEXT_PUBLIC_JWT_REFRESH_TIME) {
    logger.warn(
      'NEXT_PUBLIC_JWT_REFRESH_TIME is not set, using default of 2 hours',
    );
  }

  useEffect(() => {
    const isActive = session?.active ?? false;
    setToken(isActive);

    if (!isActive && !sessionIsLoading) {
      document.cookie = 'ory_session_token=; Max-Age=0; path=/';
      localStorage.removeItem('auth-context');
      localStorage.removeItem('apollo-client-auth-jwt');
      localStorage.removeItem('auth-machine-state');
      resetState();
    }
  }, [session, sessionIsLoading, resetState, router]);

  // Refresh session periodically
  useEffect(() => {
    // Setup interval to refresh token
    const interval = setInterval(() => {
      refetch();
    }, NEXT_PUBLIC_JWT_REFRESH_TIME);

    return () => clearInterval(interval);
  }, [refetch]);

  return (
    <ApolloNextAppProvider makeClient={getClient}>
      {children}
    </ApolloNextAppProvider>
  );
};

export { ApolloClientProvider };
